import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { marketsAPI, predictionsAPI } from '../../services/api';
import '../../components/Layout/Layout.css';

const MARKETS = [
  { key: 'india',  label: 'India',  flag: '🇮🇳', currency: '₹' },
  { key: 'us',     label: 'US',     flag: '🇺🇸', currency: '$' },
  { key: 'crypto', label: 'Crypto', flag: '₿',   currency: '$' },
  { key: 'global', label: 'Global', flag: '🌍',  currency: '$' },
];

const SIGNAL_CONFIG = {
  STRONG_BUY:  { color: '#00ff88', bg: 'rgba(0,255,136,0.1)',  label: '🚀 STRONG BUY'  },
  BUY:         { color: '#00cc6a', bg: 'rgba(0,204,106,0.1)',  label: '✅ BUY'          },
  HOLD:        { color: '#ffb800', bg: 'rgba(255,184,0,0.1)',  label: '⏸️ HOLD'         },
  SELL:        { color: '#ff6b6b', bg: 'rgba(255,107,107,0.1)',label: '⚠️ SELL'         },
  STRONG_SELL: { color: '#ff4d4d', bg: 'rgba(255,77,77,0.1)',  label: '🔴 STRONG SELL' },
};

const PAGE_SIZE = 25;

export default function PredictionsPage() {
  const [market,    setMarket]    = useState('india');
  const [tradeType, setTradeType] = useState('long_term');
  const [allStocks, setAllStocks] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [results,   setResults]   = useState({});
  const [error,     setError]     = useState('');
  const [page,      setPage]      = useState(1);
  const [totalPages,setTotalPages]= useState(1);
  const [total,     setTotal]     = useState(0);

  useEffect(() => { fetchStocks(1); setResults({}); }, [market]);

  const fetchStocks = async (p = 1) => {
    setLoading(true);
    setPage(p);
    try {
      const data = await marketsAPI.getLiveQuotes(market, p, PAGE_SIZE);
      // Handle both paginated and non-paginated responses
      if (data && data.items) {
        setAllStocks(data.items);
        setTotalPages(data.total_pages || 1);
        setTotal(data.total || data.items.length);
      } else if (Array.isArray(data)) {
        setAllStocks(data);
        setTotalPages(1);
        setTotal(data.length);
      }
    } catch(e) { console.error(e); } finally { setLoading(false); }
  };

  const analyzeStock = async (stock) => {
    setAnalyzing(stock.symbol);
    setError('');
    try {
      const result = await predictionsAPI.analyzeStock({
        symbol:         stock.symbol,
        name:           stock.name,
        market:         market,
        trade_type:     tradeType,
        current_price:  stock.price,
        change:         stock.change,
        change_percent: stock.change_percent,
        day_high:       stock.day_high,
        day_low:        stock.day_low,
        include_world_context: true,
        include_technicals:    true,
      });
      setResults(prev => ({ ...prev, [stock.symbol]: result }));
    } catch(e) {
      const msg = e.response?.data?.detail || e.message;
      setError(msg.includes('No AI API') ? 'No AI API keys configured. Please go to Settings.' : msg);
    } finally { setAnalyzing(null); }
  };

  const analyzeAll = async () => {
    setError('');
    for (const stock of allStocks.slice(0, 5)) {
      if (!results[stock.symbol]) {
        await analyzeStock(stock);
        await new Promise(r => setTimeout(r, 500));
      }
    }
  };

  const fmtPrice = (val, currency = '₹') => {
    if (!val) return '--';
    return `${currency}${parseFloat(val).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  const currency = MARKETS.find(m => m.key === market)?.currency || '₹';

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div className="page-title">🤖 AI Predictions</div>
          <div className="page-subtitle">
            AI analysis using RSI, MACD, Moving Averages, World News & Technical Indicators
            {total > 0 && <span style={{ marginLeft:'12px', color:'#00ff88' }}>{total} stocks available</span>}
          </div>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <Link to="/settings" className="btn btn-outline">⚙️ AI Keys</Link>
          <button className="btn btn-primary" onClick={analyzeAll} disabled={!!analyzing || allStocks.length === 0}>
            {analyzing ? `Analyzing ${analyzing}...` : '🚀 Analyze Top 5 on Page'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background:'rgba(255,77,77,0.1)', border:'1px solid rgba(255,77,77,0.3)', borderRadius:'12px', padding:'14px 18px', marginBottom:'20px', color:'#ff6b6b', fontSize:'14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>⚠️ {error}</span>
          {error.includes('Settings') && <Link to="/settings" className="btn btn-outline" style={{ fontSize:'12px', padding:'6px 14px', textDecoration:'none' }}>Go to Settings →</Link>}
        </div>
      )}

      {/* Controls */}
      <div style={{ display:'flex', gap:'12px', marginBottom:'24px', flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', gap:'4px', background:'rgba(255,255,255,0.04)', padding:'4px', borderRadius:'10px' }}>
          {MARKETS.map(m => (
            <button key={m.key} onClick={() => { setMarket(m.key); setPage(1); }}
              style={{ padding:'7px 14px', borderRadius:'8px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'600', background: market===m.key?'#00ff88':'transparent', color: market===m.key?'#080c14':'rgba(255,255,255,0.5)' }}>
              {m.flag} {m.label}
            </button>
          ))}
        </div>
        <div style={{ display:'flex', gap:'4px', background:'rgba(255,255,255,0.04)', padding:'4px', borderRadius:'10px' }}>
          {[['long_term','📅 Long'],['short_term','⚡ Short']].map(([key,label]) => (
            <button key={key} onClick={() => setTradeType(key)}
              style={{ padding:'7px 14px', borderRadius:'8px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'600', background: tradeType===key?'#00b8ff':'transparent', color: tradeType===key?'#080c14':'rgba(255,255,255,0.5)' }}>
              {label}
            </button>
          ))}
        </div>

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div style={{ display:'flex', gap:'8px', alignItems:'center', marginLeft:'auto' }}>
            <button onClick={() => fetchStocks(page-1)} disabled={page===1 || loading}
              style={{ padding:'7px 14px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.15)', background:'transparent', color: page===1?'rgba(255,255,255,0.2)':'rgba(255,255,255,0.7)', cursor: page===1?'not-allowed':'pointer', fontSize:'13px' }}>
              ◀ Prev
            </button>
            <span style={{ fontSize:'13px', color:'rgba(255,255,255,0.5)', padding:'0 8px' }}>
              Page {page} of {totalPages}
            </span>
            <button onClick={() => fetchStocks(page+1)} disabled={page===totalPages || loading}
              style={{ padding:'7px 14px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.15)', background:'transparent', color: page===totalPages?'rgba(255,255,255,0.2)':'rgba(255,255,255,0.7)', cursor: page===totalPages?'not-allowed':'pointer', fontSize:'13px' }}>
              Next ▶
            </button>
          </div>
        )}
      </div>

      {/* Stock Cards */}
      {loading ? (
        <div className="loading"><div className="spinner"></div> Loading stocks...</div>
      ) : (
        <>
          <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)', marginBottom:'16px' }}>
            Showing {allStocks.length} stocks · Page {page} of {totalPages} · {total} total
            {Object.keys(results).length > 0 && <span style={{ marginLeft:'12px', color:'#00ff88' }}>· {Object.keys(results).length} analyzed</span>}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(360px, 1fr))', gap:'16px' }}>
            {allStocks.map(stock => {
              const result    = results[stock.symbol];
              const isAnalyzing = analyzing === stock.symbol;
              const signal    = result?.final_signal;
              const sigCfg    = signal ? SIGNAL_CONFIG[signal] : null;

              return (
                <div key={stock.symbol} className="card" style={{ padding:'18px' }}>
                  {/* Header */}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px' }}>
                    <div>
                      <div style={{ fontFamily:'Syne,sans-serif', fontWeight:'800', fontSize:'17px' }}>{stock.symbol}</div>
                      <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.35)', marginTop:'2px' }}>{stock.name}</div>
                      {stock.sector && <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.2)' }}>{stock.sector}</div>}
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontWeight:'700', fontSize:'15px' }}>{fmtPrice(stock.price, currency)}</div>
                      <div style={{ fontSize:'12px', color: stock.change_percent >= 0 ? '#00ff88' : '#ff4d4d' }}>
                        {stock.change_percent >= 0 ? '▲' : '▼'} {Math.abs(stock.change_percent||0).toFixed(2)}%
                      </div>
                    </div>
                  </div>

                  {/* Analyzing indicator */}
                  {isAnalyzing && (
                    <div style={{ padding:'12px', background:'rgba(0,184,255,0.05)', border:'1px solid rgba(0,184,255,0.15)', borderRadius:'8px', marginBottom:'10px', textAlign:'center' }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', color:'#00b8ff', fontSize:'12px' }}>
                        <div className="spinner" style={{ width:'14px', height:'14px', borderTopColor:'#00b8ff' }}></div>
                        Analyzing with RSI, MACD, News & AI...
                      </div>
                    </div>
                  )}

                  {/* Results */}
                  {result && !isAnalyzing && (
                    <div style={{ marginBottom:'10px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                        <span style={{ padding:'5px 12px', borderRadius:'20px', fontWeight:'700', fontSize:'12px', background: sigCfg?.bg, color: sigCfg?.color, border:`1px solid ${sigCfg?.color}40` }}>
                          {sigCfg?.label || signal}
                        </span>
                        <span style={{ fontSize:'12px', color:'rgba(255,255,255,0.5)' }}>
                          Confidence: <span style={{ color:'#00ff88', fontWeight:'700' }}>{result.avg_confidence}%</span>
                        </span>
                      </div>

                      {/* Target & Stop Loss */}
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'10px' }}>
                        {result.avg_target_price > 0 && (
                          <div style={{ padding:'7px 10px', background:'rgba(0,255,136,0.05)', borderRadius:'7px' }}>
                            <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.3)' }}>Target</div>
                            <div style={{ fontSize:'13px', fontWeight:'700', color:'#00ff88' }}>{fmtPrice(result.avg_target_price, currency)}</div>
                          </div>
                        )}
                        {result.provider_details?.[0]?.stop_loss > 0 && (
                          <div style={{ padding:'7px 10px', background:'rgba(255,77,77,0.05)', borderRadius:'7px' }}>
                            <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.3)' }}>Stop Loss</div>
                            <div style={{ fontSize:'13px', fontWeight:'700', color:'#ff4d4d' }}>{fmtPrice(result.provider_details[0].stop_loss, currency)}</div>
                          </div>
                        )}
                      </div>

                      {/* Provider signals */}
                      <div style={{ display:'flex', gap:'5px', flexWrap:'wrap', marginBottom:'8px' }}>
                        {result.provider_details?.map((pd, i) => {
                          const pSig = SIGNAL_CONFIG[pd.signal] || SIGNAL_CONFIG.HOLD;
                          return (
                            <div key={i} style={{ padding:'3px 9px', borderRadius:'5px', background:'rgba(255,255,255,0.04)', fontSize:'11px' }}>
                              <span style={{ color:'rgba(255,255,255,0.35)' }}>{pd.provider}: </span>
                              <span style={{ color: pSig.color, fontWeight:'700' }}>{pd.signal}</span>
                              <span style={{ color:'rgba(255,255,255,0.25)' }}> {pd.confidence}%</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Technical summary */}
                      {result.technical_summary && (
                        <div style={{ fontSize:'11px', color:'#00b8ff', padding:'6px 10px', background:'rgba(0,184,255,0.05)', borderRadius:'6px', marginBottom:'7px' }}>
                          📊 {result.technical_summary.split('|')[0]}
                        </div>
                      )}

                      {/* Global impact */}
                      {result.global_impact && (
                        <div style={{ fontSize:'11px', color:'#ffb800', padding:'6px 10px', background:'rgba(255,184,0,0.05)', borderRadius:'6px', marginBottom:'7px' }}>
                          🌍 {result.global_impact.split('|')[0]}
                        </div>
                      )}

                      {/* Reasoning */}
                      {result.combined_reasoning && (
                        <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.5)', lineHeight:'1.4' }}>
                          {result.combined_reasoning.split('|')[0].substring(0, 150)}...
                        </div>
                      )}
                    </div>
                  )}

                  {/* Analyze Button */}
                  <button
                    className={`btn ${result ? 'btn-outline' : 'btn-primary'}`}
                    style={{ width:'100%', fontSize:'13px', marginTop:'4px' }}
                    onClick={() => analyzeStock(stock)}
                    disabled={!!analyzing}>
                    {isAnalyzing ? 'Analyzing...' : result ? '🔄 Re-analyze' : '🤖 Analyze with AI'}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Bottom pagination */}
          {totalPages > 1 && (
            <div style={{ display:'flex', justifyContent:'center', gap:'8px', marginTop:'32px', alignItems:'center' }}>
              <button onClick={() => fetchStocks(1)} disabled={page===1}
                style={{ padding:'8px 16px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.15)', background:'transparent', color: page===1?'rgba(255,255,255,0.2)':'rgba(255,255,255,0.7)', cursor: page===1?'not-allowed':'pointer' }}>
                « First
              </button>
              <button onClick={() => fetchStocks(page-1)} disabled={page===1}
                style={{ padding:'8px 16px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.15)', background:'transparent', color: page===1?'rgba(255,255,255,0.2)':'rgba(255,255,255,0.7)', cursor: page===1?'not-allowed':'pointer' }}>
                ◀ Prev
              </button>
              {Array.from({length: totalPages}, (_,i) => i+1).filter(p => Math.abs(p-page) <= 2).map(p => (
                <button key={p} onClick={() => fetchStocks(p)}
                  style={{ padding:'8px 14px', borderRadius:'8px', border:'1px solid', cursor:'pointer', fontSize:'13px', fontWeight:'600', background: p===page?'#00ff88':'transparent', borderColor: p===page?'#00ff88':'rgba(255,255,255,0.15)', color: p===page?'#080c14':'rgba(255,255,255,0.6)' }}>
                  {p}
                </button>
              ))}
              <button onClick={() => fetchStocks(page+1)} disabled={page===totalPages}
                style={{ padding:'8px 16px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.15)', background:'transparent', color: page===totalPages?'rgba(255,255,255,0.2)':'rgba(255,255,255,0.7)', cursor: page===totalPages?'not-allowed':'pointer' }}>
                Next ▶
              </button>
              <button onClick={() => fetchStocks(totalPages)} disabled={page===totalPages}
                style={{ padding:'8px 16px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.15)', background:'transparent', color: page===totalPages?'rgba(255,255,255,0.2)':'rgba(255,255,255,0.7)', cursor: page===totalPages?'not-allowed':'pointer' }}>
                Last »
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
